import { createContext, useEffect, useRef, useState } from "react";
import { io } from "socket.io-client";
import { useDispatch } from "react-redux";
import { setBookingIdStore } from "./store/Newslices/bookingIdSlice";
import { setQueryIdStore } from "./store/Newslices/queryIdSlice";
import config from "./config";
import { refreshSession, endSession } from "./axios/api";
export const ManageSocketContext = createContext(null);

const ManageSocketIoContext = ({ children }) => {
  const socketRef = useRef(null);
  const [socketInstance, setSocketInstance] = useState(null);
  const [receivingResponse, setReceivingResponse] = useState(null);
  const [hasMore, setHasMore] = useState('');
  const [hasnodata, sethasnodata] = useState({});
  const [bookingId, setBookingId] = useState(null);
  const [queryId, setQueryId] = useState(null);
  const dispatch = useDispatch();

  const safeParse = (data) => {
    if (typeof data === "string") {
      try {
        return JSON.parse(data);
      } catch (e) {
        console.error("JSON parse error:", e, data);
        return data;
      }
    }
    return data;
  };

  useEffect(() => {
    // The access token lives only in a Secure httpOnly cookie now (Task
    // #99) — there is no readable token to hand over via `auth.token`
    // anymore. `withCredentials` makes the browser attach that cookie to the
    // socket.io handshake instead; the server verifies it there the same way
    // it verifies any other protected request (see socket.io.service.js).
    // This effect only runs inside PageLayout, which itself only renders
    // behind the hardened ProtectedRoute, so a session is already confirmed
    // active by the time this connects.
    const socket = io(config.baseUrl, {
      transports: ["websocket"],
      reconnection: true,
      withCredentials: true,
    });

    socketRef.current = socket;
    setSocketInstance(socket);

    // The access_token cookie is short-lived (~10 min). Once it expires the
    // server's handshake middleware rejects the socket with "Authentication
    // error", and socket.io-client never auto-reconnects after a
    // middleware rejection — the socket would stay dead and live rates would
    // silently never arrive. So on that error, refresh the session (which
    // re-sets the access cookie) and reconnect, capped to avoid a loop.
    const MAX_AUTH_RETRIES = 2;
    let authRetries = 0;

    socket.on("connect", () => {
      authRetries = 0;
      console.log("Connected to socket.io server");
    });

    socket.on('error', (err) => {
      console.error('Socket error:', err);
    });

    socket.on('connect_error', async (err) => {
      console.error('Connection error:', err.message);
      if (err.message !== "Authentication error") return;
      if (authRetries >= MAX_AUTH_RETRIES) {
        endSession();
        return;
      }
      authRetries += 1;
      try {
        await refreshSession();
        socket.connect();
      } catch (refreshErr) {
        console.error("Socket session refresh failed:", refreshErr?.message);
        endSession();
      }
    });

    socket.on("bookingId", (data) => {
      const parsed = safeParse(data);
      setBookingId(parsed?.bookingId);
      dispatch(setBookingIdStore(parsed?.bookingId));
    });

    socket.on("queryId", (data) => {
      const parsed = safeParse(data);
      setQueryId(parsed?.queryId);
      dispatch(setQueryIdStore(parsed?.queryId));
    });

    socket.on("hasmore", (data) => {
      const parsed = safeParse(data);
      setHasMore(parsed?.data);
    });

    socket.on("hasnodata", (data) => {
      const parsed = safeParse(data);
      sethasnodata(parsed?.data);
    });

    socket.on("OfflineRates_res", (data) => {
      const parsed = safeParse(data);
      setReceivingResponse((prev) => ({ ...prev, OfflineRates_res: parsed }));
    });
    socket.on("MQ_res", (data) => {
      const parsed = safeParse(data);
      setReceivingResponse((prev) => ({ ...prev, MQ_res: parsed }));
    });
    // Emitted by the backend alongside MQ_res on a manual-quote search. Wire it
    // up here in the global socket set (alongside the other airline sockets), not
    // only in the manual-quote-only flow, so the dummy rows are captured whenever
    // the manual quote fires during a normal live search too.
    socket.on("Mannualdummydata", (data) => {
      const parsed = safeParse(data);
      setReceivingResponse((prev) => ({ ...prev, Mannualdummydata: parsed }));
    });

    socket.on("TK_res", (data) => {
      const parsed = safeParse(data);
      setReceivingResponse((prev) => ({ ...prev, TK_res: parsed }));
    });

    socket.on("QR_res", (data) => {
      const parsed = safeParse(data);
      setReceivingResponse((prev) => ({ ...prev, QR_res: parsed }));
    });

    socket.on("LH_res", (data) => {
      const parsed = safeParse(data);
      setReceivingResponse((prev) => ({ ...prev, LH_res: parsed }));
    });

    socket.on("FS_AC", (data) => {
      const parsed = safeParse(data);
      setReceivingResponse((prev) => ({ ...prev, FS_AC: parsed }));
    });

    socket.on("FS_BA", (data) => {
      const parsed = safeParse(data);
      setReceivingResponse((prev) => ({ ...prev, FS_BA: parsed }));
    });

    socket.on("FS_TK", (data) => {
      const parsed = safeParse(data);
      setReceivingResponse((prev) => ({ ...prev, FS_TK: parsed }));
    });

    socket.on("FS_AY", (data) => {
      const parsed = safeParse(data);
      setReceivingResponse((prev) => ({ ...prev, FS_AY: parsed }));
    });

    socket.on("FS_VS", (data) => {
      const parsed = safeParse(data);
      setReceivingResponse((prev) => ({ ...prev, FS_VS: parsed }));
    });

    socket.on("FS_LX", (data) => {
      const parsed = safeParse(data);
      setReceivingResponse((prev) => ({ ...prev, FS_LX: parsed }));
    });

    socket.on("FS_AZ", (data) => {
      const parsed = safeParse(data);
      setReceivingResponse((prev) => ({ ...prev, FS_AZ: parsed }));
    });

    socket.on("FS_KL", (data) => {
      const parsed = safeParse(data);
      setReceivingResponse((prev) => ({ ...prev, FS_KL: parsed }));
    });

    socket.on("FS_AF", (data) => {
      const parsed = safeParse(data);
      setReceivingResponse((prev) => ({ ...prev, FS_AF: parsed }));
    });

    socket.on("FS_EY", (data) => {
      const parsed = safeParse(data);
      setReceivingResponse((prev) => ({ ...prev, FS_EY: parsed }));
    });

    socket.on("disconnect", () => {
      console.log("Socket disconnected");
    });

    return () => {
      socket.disconnect();
    };
  }, []);

  return (
    <ManageSocketContext.Provider
      value={{
        socket: socketInstance,
        hasMore,
        hasnodata,
        bookingId,
        queryId,
        receivingResponse,
        setReceivingResponse,
      }}
    >
      {children}
    </ManageSocketContext.Provider>
  );
};

export default ManageSocketIoContext;
